import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Lang } from '../domain/types';
import { en, type MessageKey } from './en';
import { he } from './he';

const dictionaries: Record<Lang, Record<MessageKey, string>> = { en, he };
const LANG_KEY = 'edry.lang';

export type Params = Record<string, string | number | undefined>;

export function translate(lang: Lang, key: MessageKey, params?: Params): string {
  let s = dictionaries[lang][key] ?? dictionaries.en[key] ?? key;
  if (params) {
    for (const [k, v] of Object.entries(params)) s = s.split(`{${k}}`).join(v === undefined ? '' : String(v));
  }
  return s;
}

interface I18nValue {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  setLang: (l: Lang) => void;
  t: (key: MessageKey, params?: Params) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function detectInitialLang(): Lang {
  try {
    const stored = localStorage.getItem(LANG_KEY);
    if (stored === 'en' || stored === 'he') return stored;
  } catch {
    /* ignore */
  }
  if (typeof navigator !== 'undefined' && navigator.language?.toLowerCase().startsWith('he')) return 'he';
  return 'en';
}

export function LanguageProvider({ children, onChange }: { children: ReactNode; onChange?: (lang: Lang) => void }) {
  const [lang, setLangState] = useState<Lang>(detectInitialLang);
  const dir: 'ltr' | 'rtl' = lang === 'he' ? 'rtl' : 'ltr';

  useEffect(() => {
    const root = document.documentElement;
    root.lang = lang;
    root.dir = dir;
    root.setAttribute('data-lang', lang);
  }, [lang, dir]);

  const setLang = useCallback(
    (l: Lang) => {
      setLangState(l);
      try {
        localStorage.setItem(LANG_KEY, l);
      } catch {
        /* ignore */
      }
      onChange?.(l);
    },
    [onChange],
  );

  const t = useCallback((key: MessageKey, params?: Params) => translate(lang, key, params), [lang]);
  const value = useMemo(() => ({ lang, dir, setLang, t }), [lang, dir, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const v = useContext(I18nContext);
  if (!v) throw new Error('LanguageProvider missing');
  return v;
}

export function useT() {
  return useI18n().t;
}

export function useLang(): Lang {
  return useI18n().lang;
}

export type { MessageKey };
